import * as yup from 'yup';

import { screenType } from '../../../../constants/enum';
import i18n from '../../../../i18n/configs';

import type { SchemaLike } from 'yup/lib/types';

const requiredValidation = yup
  .string()
  .required(i18n.t('validation.error.required'));

const checkboxValidate = function (value: boolean[]): boolean {
  return value.includes(true);
};

export const clientSchema = (screen: number): yup.ObjectSchema<any> => {
  let clientRegisterIssueSchema = yup.object().shape({
    applicationDateFrom: yup
      .date()
      .typeError(i18n.t('validation.error.date'))
      .nullable()
      .transform((value, originalValue): void =>
        String(originalValue).trim() === '' ? null : value
      )
      .required(i18n.t('validation.error.required')),
    applicationDateTo: yup
      .date()
      .typeError(i18n.t('validation.error.date'))
      .when('applicationDateFrom', (st: Date) => {
        if (st) {
          return yup
            .date()
            .typeError(i18n.t('validation.error.date'))
            .min(st, i18n.t('validation.error.dateMin'));
        }
        return yup.date().typeError(i18n.t('validation.error.date'));
      })
      .transform((value, originalValue): void =>
        String(originalValue).trim() === '' ? null : value
      )
      .required(i18n.t('validation.error.required')),
    budget: yup
      .number()
      .when(
        'budgetCheck',
        (budgetCheck: boolean[], schema: yup.NumberSchema): SchemaLike => {
          if (!budgetCheck[1]) {
            return schema.min(
              1,
              i18n.t('validation.error.required')
            ) as SchemaLike;
          }
          return schema.strip() as SchemaLike;
        }
      ),
    budgetCheck: yup.array().of(yup.bool()),
    castingFlg: yup.bool().nullable(),
    client: yup.string().when('type', {
      is: true,
      otherwise: yup.string().nullable(),
      then: requiredValidation
    }),
    genre: yup.number().min(1, i18n.t('validation.error.required')),
    genreOther: yup.string().when('genre', {
      is: 99,
      otherwise: yup.string().nullable(),
      then: requiredValidation.max(250, i18n.t('validation.error.below250'))
    }),
    kpi: yup.array().of(yup.bool()),
    kpiOtherText: yup.string().when('kpi.10', {
      is: true,
      otherwise: yup.string().notRequired(),
      then: requiredValidation.max(250, i18n.t('validation.error.below250'))
    }),
    materialFlg: yup.bool().nullable(),
    mediaType: yup
      .array()
      .of(yup.bool())
      .when('mediaTypeCheck.1', {
        is: (value: boolean) => !value,
        otherwise: yup.array().nullable(),
        then: yup
          .array()
          .test(
            'checkboxValidate',
            i18n.t('validation.error.atLeastOneOf'),
            (value) => checkboxValidate(value as boolean[])
          )
      }),
    menu: yup
      .array()
      .of(yup.bool())
      .when('menuCheck.1', {
        is: (value: boolean) => !value,
        otherwise: yup.array().nullable(),
        then: yup
          .array()
          .test(
            'checkboxValidate',
            i18n.t('validation.error.atLeastOneOf'),
            (value) => checkboxValidate(value as boolean[])
          )
      }),
    menuOther: yup.string().when('menu.99', {
      is: (value: boolean) => value,
      otherwise: yup.string().nullable(),
      then: requiredValidation.max(250, i18n.t('validation.error.below250'))
    }),
    merchandiseType: yup
      .bool()
      .nullable()
      .oneOf([true, false], i18n.t('validation.error.required')),
    name: requiredValidation.max(250, i18n.t('validation.error.below250')),
    option: yup.array().of(yup.bool()),
    productInformation: requiredValidation.max(
      2000,
      i18n.t('validation.error.below2000')
    ),
    purpose1: yup.number().min(1, i18n.t('validation.error.required')),
    purpose1Other: yup.string().when('purpose1', {
      is: 99,
      otherwise: yup.string().nullable(),
      then: requiredValidation
        .nullable()
        .max(250, i18n.t('validation.error.below250'))
    }),
    purpose2: yup.number(),
    purpose2Other: yup.string().when('purpose2', {
      is: 99,
      otherwise: yup.string().nullable(),
      then: requiredValidation
        .nullable()
        .max(250, i18n.t('validation.error.below250'))
    }),
    purpose3: yup.number(),
    purpose3Other: yup.string().when('purpose3', {
      is: 99,
      otherwise: yup.string().nullable(),
      then: requiredValidation
        .nullable()
        .max(250, i18n.t('validation.error.below250'))
    }),
    startDate: yup
      .date()
      .when(
        'startDateCheck',
        (
          startDateCheck: boolean[],
          schema: yup.AnySchema<any, any, any>
        ): SchemaLike => {
          if (!startDateCheck[1]) {
            return schema.typeError(
              i18n.t('validation.error.date')
            ) as SchemaLike;
          }
          return schema.nullable() as SchemaLike;
        }
      ),
    startDateCheck: yup.array().of(yup.bool()),
    targetArea: yup
      .string()
      .nullable()
      .max(250, i18n.t('validation.error.below250')),
    targetClient: requiredValidation
      .typeError(i18n.t('validation.error.required'))
      .max(2000, i18n.t('validation.error.below2000'))
  });

  if (screen === screenType.agent) {
    clientRegisterIssueSchema = clientRegisterIssueSchema.shape({
      client: yup.string().min(2, i18n.t('validation.error.required'))
    });
  }

  return clientRegisterIssueSchema;
};

const urlValidation = yup.string().url(i18n.t('validation.error.url'));

export const clientRegisterOrientationSchema = yup.object().shape({
  appealPoint: yup
    .string()
    .nullable()
    .max(2000, i18n.t('validation.error.below2000')),
  article: yup.array(),
  articleOther: yup.string().when('article.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .max(250, i18n.t('validation.error.below250'))
      .nullable()
  }),
  attention: yup.array(),
  attentionOther: yup.string().when('attention.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .max(250, i18n.t('validation.error.below250'))
      .nullable()
  }),
  launchDate: yup
    .date()
    .typeError(i18n.t('validation.error.date'))
    .nullable()
    .transform((value, originalValue): void =>
      String(originalValue).trim() === '' ? null : value
    ),
  price: yup.string().nullable().max(250, i18n.t('validation.error.below250')),
  product: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  productExplain: yup
    .string()
    .nullable()
    .max(2000, i18n.t('validation.error.below2000')),
  productUrl: urlValidation
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  proposal: yup.array(),
  proposalOther: yup.string().when('proposal.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .max(250, i18n.t('validation.error.below250'))
      .nullable()
  }),
  reputation: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  salesChannel: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250'))
});
